import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Map,
  MapEvent,
  RenderingType,
  useMap,
} from '@vis.gl/react-google-maps';
import debounce from 'lodash.debounce';

import { Progress } from '@atom/mui';

import DrawingLayer from './drawing/DrawingLayer';
import DrawingToolbar from './drawing/DrawingToolbar';
import EditTargetMarkers from './drawing/EditTargetMarkers';
import useMapDrawing from './drawing/useMapDrawing';
import useStreetView from './hooks/useStreetView';
import TrafficLayer from './layers/TrafficLayer';
import MapStats from './stats/MapStats';
import EditableSearchBox from './toolbar/EditableSearchBox';
import LayerControls from './toolbar/LayerControls';
import MapCompass from './toolbar/MapCompass';
import SearchBox from './toolbar/SearchBox';
import StyleControls from './toolbar/StyleControls';
import { AtomMapProps } from './AtomMap';
import { useAtomMap } from './AtomMapProvider';
import {
  DEFAULT_MAP_HEADING,
  DEFAULT_MAP_TILT,
  GOOGLE_MAPS_MAP_IDS,
} from './mapConfig';
import { boundsToCoordinates } from './mapUtilities';
import RecenterListener from './RecenterListener';
import TaskBoundingBoxFramer from './TaskBoundingBoxFramer';

import classes from './atomMap.module.css';

// Delay before a style/POI change remounts the <Map>, so a burst of toggles
// remounts once instead of per click.
const MAP_ID_DEBOUNCE_MS = 300;

const AtomMapView = ({
  initialViewState,
  onIdle,
  onSearchChange,
  search = 'plain',
  showLayerControls = true,
  showStyleControls = true,
  showStreetView = true,
  showTiltControls = true,
  loading = false,
  editing,
  drawing,
  recenter,
  frameTask,
  customToolbars,
  compactToolbars = false,
  children,
}: AtomMapProps) => {
  const map = useMap();
  const { preferences } = useAtomMap();
  const { features, mode, onChange, onGrab, editingSlice, drawingSlice } =
    useMapDrawing({ editing, drawing });

  const nextMapId =
    GOOGLE_MAPS_MAP_IDS[preferences.mapStyle][
      preferences.poi ? 'poi' : 'noPoi'
    ];

  const [mapId, setMapId] = useState(nextMapId);

  const setMapIdDebounced = useMemo(
    () => debounce(setMapId, MAP_ID_DEBOUNCE_MS),
    [],
  );

  useEffect(() => {
    setMapIdDebounced(nextMapId);
    return () => setMapIdDebounced.cancel();
  }, [nextMapId, setMapIdDebounced]);

  const { streetViewVisible } = useStreetView();

  const mapRef = useRef<google.maps.Map | null>(null);

  useEffect(() => {
    if (map) {
      mapRef.current = map;
    }
  }, [map]);

  useEffect(
    () => () =>
      mapRef.current?.moveCamera({
        heading: DEFAULT_MAP_HEADING,
        tilt: DEFAULT_MAP_TILT,
      }),
    [],
  );

  const emitViewState = (mapData: google.maps.Map) => {
    const center = mapData.getCenter();
    const zoom = mapData.getZoom();
    const bounds = mapData.getBounds();

    if (!center || zoom === undefined) {
      return;
    }

    onIdle?.({
      center: {
        lat: center.lat(),
        lng: center.lng(),
      },
      zoom: Math.round(zoom),
      coordinates: bounds ? boundsToCoordinates(mapData) : null,
    });
  };

  const handleIdle = (event: MapEvent) => emitViewState(event.map);

  useEffect(() => {
    if (map?.getBounds()) {
      emitViewState(map);
    }
  }, [map]);

  return (
    <div className={classes.mapRoot}>
      <Map
        key={mapId}
        mapId={mapId}
        renderingType={RenderingType.VECTOR}
        reuseMaps
        gestureHandling="greedy"
        mapTypeId={preferences.mapTypeId}
        defaultCenter={initialViewState.center}
        defaultZoom={initialViewState.zoom}
        onIdle={handleIdle}
        mapTypeControl={false}
        fullscreenControl={false}
        zoomControl
        scaleControl
        cameraControl={showTiltControls}
        tiltInteractionEnabled={showTiltControls}
        headingInteractionEnabled={showTiltControls}
        isFractionalZoomEnabled
        streetViewControl={showStreetView}
      >
        {/* Layers and Markers */}
        {children}
        {showTiltControls && <MapCompass />}
        {recenter && <RecenterListener {...recenter} />}
        {frameTask && <TaskBoundingBoxFramer {...frameTask} />}
        {preferences.traffic && <TrafficLayer />}
        {editingSlice && <EditTargetMarkers editing={editingSlice} />}
        {(editingSlice || drawingSlice) && (
          <DrawingLayer
            features={features}
            onChange={onChange}
            mode={mode}
            onGrab={onGrab}
            lineColor={editingSlice?.selected?.color}
          />
        )}
      </Map>
      {/* Toolbars */}
      {!streetViewVisible && (
        <div className={classes.controls}>
          <div
            className={`${classes.toolbar}${
              compactToolbars ? ` ${classes.toolbarCompact}` : ''
            }`}
            data-cy="atomMapToolbar"
          >
            <div className={classes.spinner}>
              {loading && <Progress size={20} thickness={2} />}
            </div>
            {search === 'editable' && editingSlice ? (
              <EditableSearchBox
                editing={editingSlice}
                initialSearchTerm={initialViewState.searchTerm}
                onSearchChange={onSearchChange}
                compactToolbars={compactToolbars}
              />
            ) : search === 'plain' ? (
              <SearchBox
                initialSearchTerm={initialViewState.searchTerm}
                onSearchChange={onSearchChange}
                compactToolbars={compactToolbars}
              />
            ) : null}
            {customToolbars}
            {showStyleControls && <StyleControls />}
            {drawingSlice && <DrawingToolbar drawing={drawingSlice} />}
            {showLayerControls && <LayerControls />}
          </div>
          {drawingSlice && <MapStats drawing={drawingSlice} />}
        </div>
      )}
    </div>
  );
};

export default AtomMapView;
